/**
 * نظام مطابقة وتحديد فئة الطالب التعليمية (Student Tier Resolver)
 * 
 * المستويات التعليمية (2 Tiers):
 * 1. فئة الصغار (kids): الروضة والصفوف من الأول إلى الخامس (الابتدائي / الأعمار أقل من 11 سنة).
 * 2. فئة الكبار (senior): الصفوف من السادس إلى الثاني عشر والجامعة (الإعدادي والثانوي / الأعمار 11 سنة فما فوق).
 */

export const STUDENT_TIERS = {
  KIDS: 'kids',
  TEEN: 'teen',
  SENIOR: 'senior',
};

export const DEFAULT_STUDENT_TIER = STUDENT_TIERS.SENIOR;
export const STUDENT_TIER_STORAGE_KEY = 'student_tier';

/**
 * استرجاع فئة الطالب المخزنة في التخزين المحلي
 * @returns {string|null}
 */
export function getStoredStudentTier() {
  try {
    const stored = localStorage.getItem(STUDENT_TIER_STORAGE_KEY);
    if (stored && Object.values(STUDENT_TIERS).includes(stored)) {
      return stored;
    }
    return null;
  } catch (err) {
    void err;
    return null;
  }
}

/**
 * حفظ فئة الطالب في التخزين المحلي
 * @param {string} tier
 */
export function setStoredStudentTier(tier) {
  try {
    if (tier && Object.values(STUDENT_TIERS).includes(tier)) {
      localStorage.setItem(STUDENT_TIER_STORAGE_KEY, tier);
    }
  } catch (err) {
    void err;
  }
}

/**
 * مسح فئة الطالب من التخزين المحلي
 */
export function clearStoredStudentTier() {
  try {
    localStorage.removeItem(STUDENT_TIER_STORAGE_KEY);
  } catch (err) {
    void err;
  }
}

/**
 * استخراج رقم الصف من النص أو الرمز
 * @param {string|number|null|undefined} input
 * @returns {number|'KG'|null}
 */
export function extractGradeNumber(input) {
  if (input === null || input === undefined) return null;
  const str = String(input).trim().toLowerCase();
  if (!str) return null;

  // التحقق من مرحلة الروضة والتمهيدي
  if (str.includes('kg') || str.includes('kindergarten') || str.includes('روضة') || str.includes('تمهيدي') || str === 'k') {
    return 'KG';
  }

  // مطابقة الأرقام المباشرة مثل G1, G10, Grade 10, الصف 10
  const match = str.match(/\d+/);
  if (match) {
    const num = parseInt(match[0], 10);
    if (!isNaN(num)) return num;
  }

  // مطابقة الكلمات العربية للأرقام
  const arabicWordGrades = {
    'أول': 1, 'اول': 1, 'الأول': 1, 'الاول': 1,
    'ثاني': 2, 'الثاني': 2,
    'ثالث': 3, 'الثالث': 3,
    'رابع': 4, 'الرابع': 4,
    'خامس': 5, 'الخامس': 5,
    'سادس': 6, 'السادس': 6,
    'سابع': 7, 'السابع': 7,
    'ثامن': 8, 'الثامن': 8,
    'تاسع': 9, 'التاسع': 9,
    'عاشر': 10, 'العاشر': 10,
    'حادي عشر': 11, 'الحادي عشر': 11,
    'ثاني عشر': 12, 'الثاني عشر': 12, 'توجيهي': 12,
  };

  for (const [word, val] of Object.entries(arabicWordGrades)) {
    if (str.includes(word)) {
      return val;
    }
  }

  return null;
}

/**
 * احتساب العمر التقريبي من تاريخ الميلاد
 * @param {string|Date|null|undefined} birthDate
 * @returns {number|null}
 */
export function calculateAgeFromBirthDate(birthDate) {
  if (!birthDate) return null;
  try {
    let year, month, day;

    if (birthDate instanceof Date) {
      if (isNaN(birthDate.getTime())) return null;
      year = birthDate.getFullYear();
      month = birthDate.getMonth();
      day = birthDate.getDate();
    } else if (typeof birthDate === 'string') {
      const clean = birthDate.trim();
      // 1. ISO format: YYYY-MM-DD or YYYY/MM/DD or YYYY-MM-DDTHH:mm:ss
      const isoMatch = clean.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
      if (isoMatch) {
        year = parseInt(isoMatch[1], 10);
        month = parseInt(isoMatch[2], 10) - 1;
        day = parseInt(isoMatch[3], 10);
      } else {
        // 2. Day-first or Month-first format: DD/MM/YYYY or MM/DD/YYYY
        const dmyMatch = clean.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
        if (dmyMatch) {
          const part1 = parseInt(dmyMatch[1], 10);
          const part2 = parseInt(dmyMatch[2], 10);
          year = parseInt(dmyMatch[3], 10);
          if (part1 > 12) {
            day = part1;
            month = part2 - 1;
          } else if (part2 > 12) {
            month = part1 - 1;
            day = part2;
          } else {
            // Default to DD/MM/YYYY for standard localized dates
            day = part1;
            month = part2 - 1;
          }
        } else {
          const parsed = new Date(clean);
          if (isNaN(parsed.getTime())) return null;
          year = parsed.getFullYear();
          month = parsed.getMonth();
          day = parsed.getDate();
        }
      }
    } else {
      const parsed = new Date(birthDate);
      if (isNaN(parsed.getTime())) return null;
      year = parsed.getFullYear();
      month = parsed.getMonth();
      day = parsed.getDate();
    }

    if (!year || isNaN(year) || isNaN(month) || isNaN(day)) return null;

    const now = new Date();
    let age = now.getFullYear() - year;
    const monthDiff = now.getMonth() - month;
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < day)) {
      age--;
    }
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

/**
 * Check whether a student / child user is under age 10.
 * Follows FE rule: age !== null && age < 10 (or computed from birth_date).
 * @param {Object|null|undefined} userOrStudent
 * @returns {boolean}
 */
export function isChildUnderAge10(userOrStudent) {
  if (!userOrStudent) return false;
  if (typeof userOrStudent.age === 'number') {
    return userOrStudent.age < 10;
  }
  const birthDate = userOrStudent.birth_date || userOrStudent.birthDate;
  if (birthDate) {
    const age = calculateAgeFromBirthDate(birthDate);
    return age !== null && age < 10;
  }
  return false;
}

/**
 * تحديد الفئة استناداً إلى رقم الصف الدراسي (نظام 3 Tiers)
 * @param {number|'KG'|null} gradeNum
 * @returns {string|null}
 */
export function resolveTierByGradeNumber(gradeNum) {
  if (gradeNum === 'KG') return STUDENT_TIERS.KIDS;
  if (typeof gradeNum !== 'number') return null;

  // فئة الصغار: الروضة والصفوف من 1 إلى 5
  if (gradeNum >= 1 && gradeNum <= 5) {
    return STUDENT_TIERS.KIDS;
  }
  // فئة اليافعين: الصفوف من 6 إلى 10 (الأعمار 12 إلى 16)
  if (gradeNum >= 6 && gradeNum <= 10) {
    return STUDENT_TIERS.TEEN;
  }
  // فئة الكبار: الصفوف 11 و 12 وما فوق (17 سنة فما فوق)
  if (gradeNum >= 11) {
    return STUDENT_TIERS.SENIOR;
  }

  return null;
}

/**
 * تحديد الفئة استناداً إلى العمر (نظام 3 Tiers: /kids 1-11, /teen 12-16, /app 17+)
 * @param {number|null|undefined} age
 * @returns {string|null}
 */
export function resolveTierByAge(age) {
  if (typeof age !== 'number' || isNaN(age)) return null;

  // فئة الصغار: من 1 إلى 11 سنة (/kids 1-11)
  if (age <= 11) {
    return STUDENT_TIERS.KIDS;
  }
  // فئة اليافعين: من 12 إلى 16 سنة (/teen 12-16)
  if (age >= 12 && age <= 16) {
    return STUDENT_TIERS.TEEN;
  }
  // فئة الكبار: 17 سنة فما فوق (/app 17+)
  if (age >= 17) {
    return STUDENT_TIERS.SENIOR;
  }

  return null;
}

/**
 * الدالة الرئيسية لتحديد فئة الطالب بمرونة كاملة
 * 
 * ترتيب الأولوية:
 * 1. العمر المباشر (age) - (1-11: kids, 12-16: teen, 17+: senior)
 * 2. تاريخ الميلاد (birthDate) - احتساب العمر ومطابقته (1-11: kids, 12-16: teen, 17+: senior)
 * 3. رمز الصف (gradeCode) - كحل بديل عند غياب العمر
 * 4. اسم الصف (gradeName) - كحل بديل عند غياب العمر
 * 5. الفئة الافتراضية (DEFAULT_STUDENT_TIER)
 * 
 * @param {Object} params
 * @param {string|number|null} [params.gradeCode]
 * @param {string|null} [params.gradeName]
 * @param {number|null} [params.age]
 * @param {string|Date|null} [params.birthDate]
 * @param {string|null} [params.role]
 * @param {boolean} [params.isGuest]
 * @returns {string} إحدى قيم STUDENT_TIERS ('kids' | 'teen' | 'senior')
 */
export function resolveStudentTier({ gradeCode, gradeName, age, birthDate, role, isGuest } = {}) {
  // 1. الفحص بواسطة العمر المباشر (الأولوية القصوى للعمر: 1-11 صغار، 12-16 يافعين، 17+ كبار)
  if (age !== null && age !== undefined) {
    const numAge = typeof age === 'number' ? age : parseInt(age, 10);
    const tierFromAge = resolveTierByAge(numAge);
    if (tierFromAge) {
      console.log(`%c 🎯 [تحديد الفئة]: تم التحديد بناءً على [العمر: ${numAge} سنة] -> الفئة: ${tierFromAge.toUpperCase()}`, 'color: #8b5cf6; font-weight: bold;');
      return tierFromAge;
    }
  }

  // 2. الفحص بواسطة تاريخ الميلاد
  if (birthDate) {
    const calculatedAge = calculateAgeFromBirthDate(birthDate);
    const tierFromBirth = resolveTierByAge(calculatedAge);
    if (tierFromBirth) {
      console.log(`%c 🎯 [تحديد الفئة]: تم التحديد بناءً على [تاريخ الميلاد: ${birthDate} (العمر المحسوب: ${calculatedAge})] -> الفئة: ${tierFromBirth.toUpperCase()}`, 'color: #ec4899; font-weight: bold;');
      return tierFromBirth;
    }
  }

  // 3. الفحص بواسطة رمز الصف (في حال عدم توفر العمر أو تاريخ الميلاد)
  if (gradeCode) {
    const codeNum = extractGradeNumber(gradeCode);
    const tierFromCode = resolveTierByGradeNumber(codeNum);
    if (tierFromCode) {
      console.log(`%c 🎯 [تحديد الفئة]: تم التحديد بناءً على [رمز الصف: "${gradeCode}"] -> الفئة: ${tierFromCode.toUpperCase()}`, 'color: #10b981; font-weight: bold;');
      return tierFromCode;
    }
  }

  // 4. الفحص بواسطة اسم الصف (في حال عدم توفر العمر أو تاريخ الميلاد)
  if (gradeName) {
    const nameNum = extractGradeNumber(gradeName);
    const tierFromName = resolveTierByGradeNumber(nameNum);
    if (tierFromName) {
      console.log(`%c 🎯 [تحديد الفئة]: تم التحديد بناءً على [اسم الصف: "${gradeName}"] -> الفئة: ${tierFromName.toUpperCase()}`, 'color: #3b82f6; font-weight: bold;');
      return tierFromName;
    }
  }

  // الضيوف بدون عمر أو تاريخ ميلاد محدد ينتمون افتراضياً إلى فئة الصغار (Kids)
  if (isGuest || role === 'guest' || role?.toLowerCase?.() === 'guest') {
    return STUDENT_TIERS.KIDS;
  }

  // 5. الحالة الافتراضية الاحتياطية
  console.log(`%c 🎯 [تحديد الفئة]: تم الاعتماد على [الفئة الافتراضية] -> الفئة: ${DEFAULT_STUDENT_TIER.toUpperCase()}`, 'color: #f59e0b; font-weight: bold;');
  return DEFAULT_STUDENT_TIER;
}
